// Shared by [COVERAGES]/[LOADINGS] — same row shape: a subcatchment name
// followed by any number of (category, numeric value) pairs, with a
// subcatchment repeating its own name on a continuation row once it has more
// pairs than fit on one line. Only the column semantics differ between the
// two sections (land use/percent vs pollutant/initial buildup) — kept as
// their own named types (coverages.ts/loadings.ts) rather than merged here,
// so callers never have to know this shared shape exists.

import { parseNum } from "./parseNum.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

interface PairRow {
  subcatchment: string;
  category: string;
  value: number;
}

export function readPairSection(
  raw: ReadonlyMap<string, string[][]>,
  section: string,
  valueLabel: string,
  warnings?: Set<string>
): PairRow[] {
  const rows = raw.get(section) ?? [];
  const result: PairRow[] = [];
  for (const row of rows) {
    const subcatchment = row[0];
    if (!subcatchment) continue;
    for (let k = 1; k + 1 < row.length; k += 2) {
      result.push({
        subcatchment,
        category: row[k],
        value: parseNum(
          row[k + 1],
          0,
          `[${section}] "${subcatchment}" "${row[k]}" ${valueLabel}`,
          warnings
        ),
      });
    }
  }
  return result;
}

export function writePairSection(
  raw: ReadonlyMap<string, string[][]>,
  section: string,
  rows: PairRow[]
): Map<string, string[][]> {
  const bySubcatchment = new Map<string, PairRow[]>();
  for (const row of rows) {
    const list = bySubcatchment.get(row.subcatchment) ?? [];
    list.push(row);
    bySubcatchment.set(row.subcatchment, list);
  }
  const outRows: string[][] = [];
  for (const [subcatchment, list] of bySubcatchment) {
    const outRow = [subcatchment];
    for (const row of list) outRow.push(row.category, String(row.value));
    outRows.push(outRow);
  }
  return writeOrDeleteSection(raw, section, outRows);
}
